import { DATE_FORMAT } from '@constants';
import { capitalize, removedCellClassName } from '@helpers';
import { Box, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { ITableEnterpriseUsers } from '@services';
import dayjs from 'dayjs';
import {
  StyledButtonAction,
  StyledTypography,
  StyledTypoNoAction,
} from './EnterpriseUsersTable.styles';
import { EInvitationStatus, EInvitationStatusResponse } from './enum';
import { IColumnsProps } from './types';
import { DEFAULT_TOAST_MESSAGE_RESEND_REVOKE } from './constants';

const renderEllipseBox = (value: string) => {
  return (
    <Box className="ellipsis">
      <Tooltip title={value} placement="bottom">
        <StyledTypography variant="caption" fontSize="14px">
          {value}
        </StyledTypography>
      </Tooltip>
    </Box>
  );
};

const handleRenderStatus = (status: EInvitationStatusResponse) => {
  switch (status) {
    case EInvitationStatusResponse.Accepted:
      return {
        color: '#29B15E',
        status: EInvitationStatus.Accepted,
      };
    case EInvitationStatusResponse.Pending:
      return {
        color: '#FFD800',
        status: EInvitationStatus.Pending,
      };
    case EInvitationStatusResponse.Expired:
      return {
        color: '#FF4848',
        status: EInvitationStatus.Expired,
      };
    case EInvitationStatusResponse.Revoked:
      return {
        color: '#FF4848',
        status: EInvitationStatus.Revoked,
      };
    default:
      break;
  }
};

export const getColumns = ({
  isLoadingResend,
  isLoadingRevoke,
  idInteract,
  handleClickRevoke,
  handleClickResend,
}: IColumnsProps): GridColDef<ITableEnterpriseUsers>[] => {
  return [
    {
      field: 'fullName',
      headerName: 'User Full Name',
      minWidth: 80,
      flex: 1.1,
      sortable: false,
      cellClassName: removedCellClassName,
      renderCell: ({ row: { fullName } }) => renderEllipseBox(fullName),
    },
    {
      field: 'userRole',
      headerName: 'User Role',
      minWidth: 80,
      cellClassName: removedCellClassName,
      sortable: false,
      flex: 1,
      renderCell: ({ row: { role } }) => (
        <StyledTypography>{role ? role : 'Admin'}</StyledTypography>
      ),
    },
    {
      field: 'email',
      headerName: 'User Email',
      minWidth: 80,
      flex: 1.3,
      sortable: false,
      cellClassName: removedCellClassName,
      renderCell: ({ row: { email } }) => {
        return renderEllipseBox(email);
      },
    },
    {
      field: 'acceptedAt',
      headerName: 'Join date',
      width: 110,
      flex: 1,
      sortable: false,
      cellClassName: removedCellClassName,
      renderCell: ({ row: { invitation } }) => (
        <>
          {invitation.acceptedAt && (
            <StyledTypography>{dayjs(invitation.acceptedAt).format(DATE_FORMAT)}</StyledTypography>
          )}
        </>
      ),
    },
    {
      field: 'status',
      headerName: 'Invitation Status',
      minWidth: 130,
      flex: 1,
      sortable: false,
      cellClassName: removedCellClassName,
      renderCell: ({ row: { invitation } }) => {
        const statusData = handleRenderStatus(invitation.status as EInvitationStatusResponse);
        return (
          <StyledTypography
            sx={{
              color: statusData?.color,
            }}
          >
            {capitalize(statusData?.status || '')}
          </StyledTypography>
        );
      },
    },
    {
      field: 'invitationAction',
      headerName: 'Invitation Action',
      minWidth: 180,
      flex: 1,
      sortable: false,
      cellClassName: removedCellClassName,
      renderCell: ({ row: { invitation, id } }) => {
        return (
          <>
            {invitation.status === EInvitationStatusResponse.Accepted ||
            invitation.status === EInvitationStatusResponse.Revoked ? (
              <StyledTypoNoAction>No Action Needed</StyledTypoNoAction>
            ) : (
              <Box display="flex" alignItems="center" gap="7px">
                {invitation.status === EInvitationStatusResponse.Pending && (
                  <StyledButtonAction
                    loading={isLoadingRevoke && id === idInteract}
                    onClick={() => handleClickRevoke({ invitationId: invitation.id }, id)}
                    variant="contained"
                  >
                    Revoke
                  </StyledButtonAction>
                )}

                {(invitation.status === EInvitationStatusResponse.Pending ||
                  invitation.status === EInvitationStatusResponse.Expired) && (
                  <StyledButtonAction
                    loading={isLoadingResend && id === idInteract}
                    onClick={() => handleClickResend({ invitationId: invitation.id }, id)}
                    variant="contained"
                  >
                    Resend
                  </StyledButtonAction>
                )}
              </Box>
            )}
          </>
        );
      },
    },
  ];
};

export const getErrorMessage = (errorMessage: string) => {
  switch (errorMessage) {
    case 'INVITATION_EMAIL_EXISTS_ERR':
      return 'Invitation Email Exists!';
    default:
      return DEFAULT_TOAST_MESSAGE_RESEND_REVOKE.ERROR;
  }
};
